<template>
  <div class="h-full flex flex-col">
    <div v-if="viewNotFound" class="flex flex-col items-center justify-center flex-1 h-full">
      <EmptyScreen
        :source="entityType"
        text="View not found or has been deleted."
        padding="mt-0"
        css="size-20"
      />
    </div>

    <div
      v-else-if="loading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>

    <template v-else>
      <ViewFilterComponentV2
        :source="`${entityType}_dashboard_section`"
        :filter-data="filterData"
        :is-dynamic-c-r-m="true"
        :slug="entityType"
        :service="effectiveService"
        :locked-filter-keys="allFilterKeys"
        :hide-more-options="true"
        :hide-reset-button="true"
        :hide-add-filter-button="true"
        no-grow
      />

      <DynamicCRMTable
        v-if="records?.length > 0"
        ref="dynamicCRMTableRef"
        :records="records"
        :table-headers="tableHeaders"
        :columns="columns"
        :pagination="pagination"
        :actions="actions"
        :custom-row="customRow"
        :custom-column-render="simpleCustomColumnRender"
        :entity-type="entityType"
        :schema-object-data="schemaObjectData"
        :sort-data="sortData"
        :frozen-columns="frozenColumns"
        :per-page-options="[10, 20, 50, 100]"
        :selection="selection"
        @update:pagination="handlePageChange"
        @selection-change="handleSelectionChange"
      />

      <EmptyScreen
        v-else
        :source="entityType"
        text="No results found!"
        padding="mt-20"
        css="size-20"
      />
    </template>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import {message} from "ant-design-vue";
import {imageNames} from "@/src/constants/imageConstants.js";
import {ATTRIBUTE_TYPES, SERVICE_REQUIRED_ENTITY_TYPES, pluralizeEntityType} from "@/src/constants/crmConstants.js";
import {generateTableHeadersFromSchema, transformRecordForTable,} from "@/src/utils/dynamicCRMViewUtils.js";
import {getEntityDetailRoute} from "@/src/utils/entityRouteUtils.js";
import {getTimeZone} from "@/src/utils/utils.js";
import CRMService from "@/src/api/CRMService.js";
import DashboardService from "@/src/api/DashboardService.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LastActivityColumn from "@/src/components/LastActivityColumn.vue";
import SimpleOwnerRenderer from "@/src/components/SimpleOwnerRenderer.vue";
import DynamicCRMTable from "@/src/views/dynamic_crm/DynamicCRMTable.vue";
import ViewFilterComponentV2 from "@/src/components/view_components/ViewFilterComponentV2.vue";

const props = defineProps({
  entityType: { type: String, required: true },
  viewId: { type: [Number, String], required: true },
  cardName: { type: String, default: "" },

  service: { type: String, default: null },
});

const store = useStore();
const router = useRouter();

const records = ref([]);
const loading = ref(true);
const viewNotFound = ref(false);
const schemaObjectData = ref(null);
const tableHeaders = ref([]);
const columns = ref([]);
const frozenColumns = ref([]);
const sortData = ref({});
const filterData = ref({});
const dynamicCRMTableRef = ref(null);
const views = ref({
  active: [],
  inactive: [],
  current: {},
});

const selection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const pagination = ref({
  current: 1,
  pageSize: 20,
  total: 0,
});

const users = computed(() => store.getters["users/users"] || []);
const timeZone = ref(getTimeZone());

const allFilterKeys = computed(() => Object.keys(filterData.value));

const effectiveService = computed(() => {
  if (SERVICE_REQUIRED_ENTITY_TYPES.includes(props.entityType)) {
    return props.service === 'customer_success' ? 'customer_success' : 'crm';
  }
  return null;
});

const actions = computed(() => [
  {
    label: "Edit",
    handler: (row) => showEditDrawer(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => showDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
]);

const simpleCustomColumnRender = computed(() => {
  const renders = {
    last_activity: {
      component: LastActivityColumn,
      props: (record) => ({
        updatedAt: record.updated_at,
        activity: record.last_activity || null,
        timezone: timeZone.value,
      }),
    },
  };

  if (schemaObjectData.value?.properties) {
    schemaObjectData.value.properties.forEach((property) => {
      if (property.datatype === ATTRIBUTE_TYPES.USER) {
        renders[property.slug] = {
          component: SimpleOwnerRenderer,
          props: (record) => ({
            row: record,
            ownerKey: property.slug,
          }),
        };
      }
    });
  }

  return renders;
});

const customRow = (record) => {
  const routePath = getEntityDetailRoute({
    entityType: pluralizeEntityType(props.entityType),
    recordId: record.id,
  });

  return {
    onClick: (event) => {
      const target = event.target;
      const isCheckboxClick =
        target.tagName === "INPUT" ||
        target.type === "checkbox" ||
        target.closest(".ant-checkbox-wrapper") ||
        target.closest(".ant-checkbox") ||
        target.closest(".ant-table-selection-column");
      const isLinkClick =
        target.tagName === "A" || target.closest("a");

      if (!isCheckboxClick && !isLinkClick) {
        router.push(routePath);
      }
    },
    "data-href": routePath,
    style: { cursor: "pointer" },
  };
};

const fetchSchemaObjectData = async () => {
  try {
    let existingSchema = store.getters["schema/getSchemaBySlug"](
      props.entityType,
    );
    let isLoading = store.getters["schema/isSchemaLoading"](props.entityType);

    let result;

    if (existingSchema) {
      result = { success: true, schema: existingSchema };
    } else if (isLoading) {
      const maxAttempts = 50;
      let attempts = 0;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        existingSchema = store.getters["schema/getSchemaBySlug"](
          props.entityType,
        );
        isLoading = store.getters["schema/isSchemaLoading"](props.entityType);

        if (existingSchema) {
          result = { success: true, schema: existingSchema };
          break;
        }

        if (!isLoading) {
          result = await store.dispatch("schema/fetchSchema", {
            slug: props.entityType,
          });
          break;
        }

        attempts++;
      }

      if (!result) {
        console.error("Timeout waiting for schema to load");
        return;
      }
    } else {
      result = await store.dispatch("schema/fetchSchema", {
        slug: props.entityType,
      });
    }

    if (result && result.success && result.schema) {
      const { attributes, object, relationships, sub_objects } = result.schema;

      const validAttributes =
        attributes?.filter(
          (attr) =>
            attr.name &&
            attr.name.trim() !== "" &&
            attr.slug &&
            attr.slug.trim() !== "",
        ) || [];

      schemaObjectData.value = {
        object: object,
        properties: validAttributes.map((attr) => ({
          ...attr,
          datatype: attr.type,
          required: attr.is_required,
          structured_options: attr.structured_options || null,
          formula: attr.formula || null,
          related_objects: attr.related_objects || [],
          metadata: attr.metadata || null,
        })),
        relationships: relationships || [],
        sub_objects: sub_objects || [],
      };

      tableHeaders.value = generateTableHeadersFromSchema(validAttributes, {
        excludeFields: ["id"],
        includeSystemFields: false,
      });

      const primaryProperty = validAttributes.find((attr) => attr.is_primary);
      if (primaryProperty) {
        const primaryHeader = tableHeaders.value.find(
          (header) => header.key === primaryProperty.slug,
        );
        if (primaryHeader) {
          primaryHeader.view = true;
        }
      }
    } else {
      if (result?.error !== "Already loading") {
        console.error("Failed to fetch schema object data:", result?.error);
        message.error("Failed to load page data");
      }
    }
  } catch (error) {
    console.error("Error fetching schema object data:", error);
  }
};

const showEditDrawer = (record) => {
  router.push(
    getEntityDetailRoute({
      entityType: props.entityType,
      recordId: record.id,
    }),
  );
};

const showDeleteConfirm = (record) => {
  message.info("Delete functionality not available in dashboard view");
};

const loadViewData = async () => {
  try {
    if (!props.viewId) {
      console.warn("No viewId prop provided to DynamicCRMSection");
      return;
    }

    const data = await CRMService.fetchDynamicEntityViews(props.entityType, null, effectiveService.value);

    if (data.success) {
      const activeViews = data.activeViews || [];
      const inactiveViews = data.inActiveViews || [];

      // Check if the desired view exists in the list
      const foundInList = [...activeViews, ...inactiveViews].find(
        (v) => v.id === props.viewId,
      );

      if (!foundInList) {
        console.error(`View with ID "${props.viewId}" not found`);
        viewNotFound.value = true;
        loading.value = false;
        return;
      }

      // currentView from the API response includes filters; activeViews/inactiveViews do not.
      // Use currentView if it matches, otherwise switch to the desired view.
      let viewWithFilters;
      if (data.currentView?.id === props.viewId) {
        viewWithFilters = data.currentView;
      } else {
        try {
          const switchResponse = await DashboardService.updateCurrentView(props.viewId);
          if (switchResponse?.success && switchResponse?.view) {
            viewWithFilters = switchResponse.view;
          } else {
            // Fallback: re-fetch views after switching
            const refreshed = await CRMService.fetchDynamicEntityViews(props.entityType, null, effectiveService.value);
            viewWithFilters = refreshed?.currentView || foundInList;
          }
        } catch (err) {
          console.error(`Error switching to view ${props.viewId}:`, err);
          viewWithFilters = foundInList;
        }
      }

      const currentView = {
        id: viewWithFilters.id,
        name: viewWithFilters.name,
        filters: viewWithFilters.filters || {},
        page_size: viewWithFilters.pageSize || viewWithFilters.page_size || 20,
        current_page: viewWithFilters.currentPage || viewWithFilters.current_page || 1,
        sorts: viewWithFilters.sorts || {},
        columns: viewWithFilters.columns || [],
        frozen_columns: viewWithFilters.frozen_columns || [],
      };

      views.value = {
        active: activeViews,
        inactive: inactiveViews,
        current: currentView,
      };

      filterData.value = currentView.filters || {};

      pagination.value.pageSize = currentView.page_size || 20;
      pagination.value.current = currentView?.current_page || 1;
      sortData.value = currentView.sorts || {};

      await fetchFilteredRecords();
    } else {
      console.error("Failed to fetch View Data:", data.errors);
      viewNotFound.value = true;
      loading.value = false;
    }
  } catch (error) {
    console.error("Error fetching View Data:", error);
    viewNotFound.value = true;
    loading.value = false;
  }
};

const applyFilter = async () => {
  try {
    const req_body = {
      filters: filterData.value,
      sorts: sortData.value,
      page_size: pagination.value.pageSize,
      current_page: pagination.value.current,
    };
    const data = await DashboardService.applyFilter(
      views.value.current.id,
      req_body,
    );
    if (data.success) {
      filterData.value = data.filters || {};
      if (pagination.value.selection) pagination.value.current = 1;
      await fetchFilteredRecords();
    } else {
      console.error("Failed to apply filter:", data.errors);
    }
  } catch (error) {
    console.error("Error applying filter:", error);
  }
};

const fetchFilteredRecords = async () => {
  loading.value = true;

  try {
    const response = await CRMService.fetchDynamicEntityData(
      props.entityType,
      props.viewId,
      pagination.value.current,
      pagination.value.pageSize,
      "",
      null,
    );

    if (response?.success) {
      records.value = (response?.results || []).map((record) =>
        transformRecordForTable(
          record,
          schemaObjectData.value,
          users.value,
          [],
        ),
      );

      pagination.value.total =
        records.value.length === 0
          ? 0
          : response?.total ?? records.value.length;
      pagination.value.pageSize = response?.per_page || pagination.value.pageSize;

      sortData.value = response.sorts || {};

      columns.value = (response?.columns || []).filter(
          (col) => col !== null && col !== undefined,
      );
    } else {
      console.error("Failed to fetch records:", response?.errors);
      records.value = [];
      pagination.value.total = 0;
    }
  } catch (error) {
    console.error("Error fetching records:", error);
    records.value = [];
    pagination.value.total = 0;
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  loading.value = true;
  applyFilter();
};

const handleSelectionChange = (payload) => {
  if (payload && "select_all" in payload) {
    selection.value.select_all = !!payload.select_all;
    selection.value.selected_ids = payload.selected_ids || [];
    selection.value.unselected_ids = payload.unselected_ids || [];
    return;
  }

  if (payload && Array.isArray(payload.selectedRowKeys)) {
    const { selectedRowKeys, selectAll } = payload;
    selection.value.selected_ids = selectedRowKeys;
    const totalCount = pagination.value?.total || 0;
    selection.value.select_all =
      selectAll && selectedRowKeys.length === totalCount && totalCount > 0;
    selection.value.unselected_ids = selection.value.select_all ? [] : [];
  }
};

onMounted(async () => {
  const storeUsers = store.getters["users/users"];
  if (!storeUsers || storeUsers.length === 0) {
    await store.dispatch("users/fetchUsersAndTeams");
  }

  await fetchSchemaObjectData();

  await loadViewData();
});
</script>
